import React from 'react';
import { css } from '@emotion/react';
import { Badge, Radio } from 'antd';
import type { RadioGroupProps, RadioChangeEvent } from 'antd/lib/radio';
import { legacyOnly, newDesignOnly } from 'constants/themes.constants';
import styled from 'types/theme.types';
import Tooltip from 'components/common/tooltip';

export type RadioButtonOpts<T = string> = {
  value: T;
  content: string | React.ReactElement;
  disabled?: boolean;
  ribbonText?: string;
  tooltipId?: string;
};

export interface RadioButtonsProps extends Omit<RadioGroupProps, 'onChange' | 'size'> {
  onChange?: (value: string) => void;
  buttons?: RadioButtonOpts[];
  size?: RadioGroupProps['size'] | 'huge';
  block?: boolean;
}

const RadioButtons: React.FC<RadioButtonsProps> = React.forwardRef<HTMLDivElement, RadioButtonsProps>(
  ({ onChange, buttons, size, children, ...props }, ref) => {
    const handleOnChange = (e: RadioChangeEvent) => onChange?.(e.target.value);
    const ButtonComponent = size === 'huge' ? HugeButton : Radio.Button;

    return (
      <RadioGroupContainer ref={ref} onChange={handleOnChange} {...props}>
        <Container>
          {children}
          {buttons?.map(({ value, content, disabled, ribbonText, tooltipId }) => {
            const button = (
              <Tooltip placement="top" titleId={tooltipId!}>
                <ButtonComponent data-e2e={`radio-button-${value}`} key={value} value={value} disabled={!!disabled}>
                  {size === 'huge' ? <HugeButtonContentContainer>{content}</HugeButtonContentContainer> : content}
                </ButtonComponent>
              </Tooltip>
            );
            return ribbonText ? (
              <Badge.Ribbon text={ribbonText} key={value}>
                {button}
              </Badge.Ribbon>
            ) : (
              button
            );
          })}
        </Container>
      </RadioGroupContainer>
    );
  }
);

RadioButtons.displayName = 'RadioButtons';

const RadioGroupContainer = styled(Radio.Group)`
  .ant-radio-button-wrapper::before {
    // making our big-buttons with 'border-radius' without letting this 'before' element to interrupt
    display: none;
  }
`;

const Container = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 16px;
`;

/**
 * A real variant rather than an old-design fix, so the geometry is unbranched. Only the border colour
 * branches: switch-on may not read legacy `Env0Theme` keys, which resolve through the unaudited bridge
 * mapping (D7.3). The radius keeps its `!important` in both — antd rounds only a group's end buttons, and
 * these are standalone cards.
 *
 * The resting border is `border/primary`, the same neutral Card draws. It was `border/secondary` — the
 * lime — which painted every unselected card in the selected accent while the checked one rendered in
 * the bridge's darker green, so the whole group read as chosen (APO-475 browser pass).
 */
const HugeButton = styled(Radio.Button)`
  width: 205px;
  height: 80px;
  border-radius: 4px !important;
  ${legacyOnly(
    theme => css`
      border: 1px solid ${theme.nonEditableBorderGray};
    `
  )}
  ${newDesignOnly(css`
    border: var(--border-width-xs) solid var(--border-primary);
  `)}
  padding: 0;
`;

export const HugeButtonContentContainer = styled.div`
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  font-size: 1.3em;
  user-select: none;
`;

export default RadioButtons;
